// Uploads + deliverables client.
//
// Two backend resources, related concepts: user-supplied source files
// (`/api/uploads/*`) and the cross-project deliverable gallery
// (`/api/deliverables/*`) the agent produces. Both are file-shaped, both
// surface as panels in the projects UI — grouped here for that reason.

import axios, { type AxiosError, type AxiosProgressEvent } from "axios";

import type { components } from "@/lib/api-types";

import {
  API,
  ApiError,
  authHeaders,
  awaitTenantSettled,
  tenantContextInDoubt,
  TenantChangedError,
  TenantContextUnresolvedError,
  recoverIfWorkspaceDenied,
  requestJson,
  requestOk,
  type Upload,
  type UploadsList,
} from "../api";
import {
  MAX_UPLOAD_BYTES,
  transferFailureMessage,
  usesDirectUpload,
} from "../upload-limits";
import { ResumableUploadError } from "../resumable-upload";
import { triggerAuthedDownload } from "./artifacts";
import {
  DirectUploadUnavailableError,
  type DirectUploadResume,
  uploadFileDirect,
} from "./uploads-direct";


type Schemas = components["schemas"];


// ---------------------------------------------------------------------------
// Deliverables (cross-project gallery)
// ---------------------------------------------------------------------------

// Generated from routes/deliverables.py:Deliverable. Hand-rolled was
// missing `tenant_id` and `user_id` (always emitted by both routes)
// and had `project_name` as `string | null`, but the server always
// resolves it to a string (falls back to project_id).
export type Deliverable = Schemas["Deliverable"];

export async function listDeliverables(params?: {
  project_id?: string;
  type?: string;
  task_id?: string;
  scope?: "mine" | "all";
}): Promise<Deliverable[]> {
  const qs = new URLSearchParams();
  if (params?.project_id) qs.set("project_id", params.project_id);
  if (params?.type) qs.set("type", params.type);
  if (params?.task_id) qs.set("task_id", params.task_id);
  if (params?.scope) qs.set("scope", params.scope);
  const url = `${API}/api/deliverables${qs.size ? `?${qs}` : ""}`;
  const data = await requestJson<{ deliverables: Deliverable[] }>(url);
  return data.deliverables;
}

export async function getDeliverable(deliverableId: string): Promise<Deliverable> {
  return requestJson(`${API}/api/deliverables/${encodeURIComponent(deliverableId)}`);
}

export async function deleteDeliverable(deliverableId: string): Promise<void> {
  await requestOk(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}`,
    { method: "DELETE" },
  );
}

export async function deleteDeliverablesByTask(taskId: string): Promise<void> {
  await requestOk(
    `${API}/api/deliverables?task_id=${encodeURIComponent(taskId)}`,
    { method: "DELETE" },
  );
}

// Live-artifact refresh. POSTing kicks off a hidden one-shot task that
// re-runs the same skill with the binding's recorded inputs. The result
// lands as a new version on the same deliverable (composite_key preserved).
// `refresh_task_id` is the hidden task; subscribe via the existing
// /api/tasks/{tid}/stream endpoint to follow progress.
export type DeliverableRefreshResponse = Schemas["RefreshResponse"];

export async function refreshDeliverable(
  deliverableId: string,
): Promise<DeliverableRefreshResponse> {
  return requestJson(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}/refresh`,
    { method: "POST" },
  );
}

// Revise = open a fresh task with the deliverable attached as a pill, then
// iterate freely (no per-cycle versioning). This materializes the deliverable's
// file as a task_attachment upload; the caller seeds the returned file_id into
// a new task's composer and navigates the user in.
export type DeliverableAttachResponse = Schemas["AttachResponse"];

export async function attachDeliverableToTask(
  deliverableId: string,
): Promise<DeliverableAttachResponse> {
  return requestJson(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}/attach`,
    { method: "POST" },
  );
}

/** Re-associate a deliverable (and its version series) with another project. */
export async function moveDeliverableToProject(
  deliverableId: string,
  projectId: string,
): Promise<Deliverable> {
  return requestJson(
    `${API}/api/deliverables/${encodeURIComponent(deliverableId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: projectId }),
    },
  );
}


// ---------------------------------------------------------------------------
// Uploads (sources)
// ---------------------------------------------------------------------------

export async function listUploads(opts?: { projectId?: string }): Promise<UploadsList> {
  const url = opts?.projectId
    ? `${API}/api/uploads?project_id=${encodeURIComponent(opts.projectId)}`
    : `${API}/api/uploads`;
  return requestJson(url);
}

export type MountedSource = Schemas["MountedSource"];
export type MountedSourcesList = Schemas["MountedSourcesList"];

/** Session mount paths for a project's Sources (agent manifest source of truth). */
export async function listMountedProjectSources(
  projectId: string,
): Promise<MountedSourcesList> {
  return requestJson(
    `${API}/api/uploads/mounted?project_id=${encodeURIComponent(projectId)}`,
  );
}

export async function uploadFile(
  file: File,
  opts?: {
    tags?: string[];
    projectId?: string;
    // "project_knowledge" (default, durable Source) | "task_attachment"
    // (per-chat file, hidden from Sources; composer uploads use this and the
    // server promotes them to Sources at send — ENG-3610). Match the server
    // enum.
    scope?: "project_knowledge" | "task_attachment";
    // Makes a retry of THIS upload safe. Mint once per logical upload (not per
    // attempt) with newUploadRetryState() and pass the SAME object back on
    // every retry — see UploadRetryState.
    retry?: UploadRetryState;
  },
): Promise<Upload> {
  // One implementation, two entry points. This used to POST via `fetch` with a
  // watchdog of its own, which could not tell "still uploading the body" from
  // "the server has gone quiet" — `fetch` reports no request-body progress — so
  // the timer covered the TRANSFER and aborted a perfectly healthy 30 MB upload
  // on any uplink slower than a few Mbps.
  //
  // `uploadFileWithProgress` already gets this right: axios makes body-sent
  // observable, so the finishing watchdog arms only once the last byte is gone.
  // Delegating inherits the correct behaviour instead of keeping a second,
  // worse copy of it — the only real difference between the two entry points is
  // whether the caller wants progress callbacks.
  return uploadFileWithProgress(file, {
    tags: opts?.tags,
    projectId: opts?.projectId,
    scope: opts?.scope,
    retry: opts?.retry,
  });
}

// Size limits + their user-facing copy live in a dependency-free module so
// every attach surface shares one definition and it stays unit-testable.
export {
  MAX_UPLOAD_BYTES,
  MAX_DIRECT_UPLOAD_BYTES,
  uploadTooLargeMessage,
  attachRejectionMessage,
  attachRejectionReason,
  usesDirectUpload,
} from "../upload-limits";

/**
 * Everything a retry of one logical upload needs to stay de-duplicating.
 *
 * Only the DIRECT path is idempotent today: `token`/`staged` let a retry after
 * a lost `/complete` response finish the existing staged upload instead of
 * starting a new one. The multipart path has no such handle — a post-body
 * failure there is still ambiguous and non-retryable (the caller revalidates
 * its Sources list). Giving it one is ENG-3487.
 *
 * Mint it when the upload is *queued* and pass the same object back on every
 * retry; the upload functions mutate it in place. */
export type UploadRetryState = DirectUploadResume;

/** A fresh retry state for one logical upload. */
export function newUploadRetryState(): UploadRetryState {
  return {};
}

/**
 * Whether retrying this upload de-duplicates against the first attempt.
 *
 * Mirrors the routing rule in `uploadFileWithProgress`: only the direct path
 * carries a resume handle (`token`/`staged`), so only it can retry after an
 * *ambiguous* post-body failure without risking a second Source. Callers
 * deciding whether to offer Retry should ask this rather than re-deriving the
 * size threshold, so there is one place to change when ENG-3487 gives the
 * multipart path the same guarantee.
 */
export function retryIsIdempotent(file: File): boolean {
  return usesDirectUpload(file);
}

// Resume state for callers that don't thread their own, keyed by file identity.
//
// The direct path's token is what makes a repeated `/complete` replay instead of
// duplicating, and it only works while somebody still holds it. Minting a
// throwaway state per call meant a caller without its own (`uploadManyToProject`,
// `usePendingAttachments`, the deck-skill picker) lost the token the moment the
// promise rejected — and the user's natural recovery, re-picking the same file,
// then started over at `/start` with a NEW token and created a second Source.
//
// This is de-duplication by RECONCILIATION rather than by timing, which is the
// lesson from the idempotency work that came off this PR (ENG-3487): it does not
// matter how long the server worker ran or whether it beat the client's clock,
// only that the handle used to ask about it outlived the attempt. Keying by
// identity is what makes a re-pick the SAME logical upload.
//
// NOTE: there is deliberately no module-level registry of resume handles here.
//
// One existed briefly and was cut. Its job was to let a caller that does NOT
// thread its own `UploadRetryState` — the composer, the projects rail, the
// deck-skill picker — still de-duplicate when the user re-picks the same file
// after an ambiguous finalize. Keyed by file identity, it produced eight
// distinct defects in five review rounds, the last of which were structural
// rather than fixable: file identity is not UPLOAD identity, so two concurrent
// uploads of one file cannot share one mutable entry; and a `Map` cannot
// survive the page reload that is one of the likeliest ways to lose a handle.
//
// Doing it properly means attempt-scoped identity in durable, cross-tab-safe
// storage — a real subsystem, and one the multipart path needs identically.
// That is ENG-3487; it should be designed once for both paths rather than
// accreted here. Until then, callers that want a retry to de-duplicate thread
// their own state (the Sources modal does), and callers that don't get the
// pre-existing at-least-once behaviour.

/** Whether a failure proves the resume token is dead and the upload must start
 * over from `/start`.
 *
 * A 404 from `/complete` on a token we were RESUMING is the server saying that
 * token no longer exists — expired past the replay window, or swept. Holding on
 * to it wedges the file permanently: every re-pick jumps straight back to
 * `/complete` and 404s again, never falling back to `/start`.
 *
 * Restarting cannot duplicate. The replay window is the token's advertised
 * lifetime (`INTENT_TTL_SECONDS`); past it a re-pick genuinely IS a new upload.
 *
 * Narrow on purpose. Without a token there was nothing to resume, so a 404 is
 * an ordinary error (a deleted project, say) and restarting would just repeat
 * it. And only 404 qualifies: a 409 means a worker is alive on this very token,
 * so discarding it would create the duplicate this whole mechanism prevents. */
export function isDeadResumeToken(resume: UploadRetryState, e: unknown): boolean {
  return Boolean(resume.token) && e instanceof ApiError && e.status === 404;
}

/** Whether an upload failure is permanent — i.e. Retry must NOT be offered.
 *
 * Exported and pure so it can be tested directly. It was inline in the Sources
 * modal, where a scope change silently flipped `UploadStillProcessingError`
 * from permanent to retryable and nothing caught it.
 *
 * The load-bearing case is `UploadStillProcessingError`, which only the
 * MULTIPART path raises: the request body already landed, so the Source may be
 * about to exist, and the client has no handle to de-duplicate a resubmission
 * with. Retrying can therefore create a second Source. The recovery is
 * revalidating the Sources list, not a button. (The DIRECT path is different —
 * its `DirectUploadResume` token makes a repeated `/complete` replay — which is
 * why a 409 stays retryable below. Giving multipart the same protection is
 * ENG-3487.)
 *
 * `UploadTooLargeError` is permanent by nature rather than by status: this
 * deployment cannot stage a file that size at all, so a retry reissues the same
 * failing request forever.
 *
 * Everything else keys off the status: a 4xx will just re-fail, except the
 * transient ones (408 timeout, 409 already-processing, 429 rate-limited). */
export function isPermanentUploadFailure(e: unknown): boolean {
  if (e instanceof UploadStillProcessingError) return true;
  if (e instanceof UploadTooLargeError) return true;
  return (
    e instanceof ApiError &&
    e.status >= 400 &&
    e.status < 500 &&
    e.status !== 408 &&
    e.status !== 409 &&
    e.status !== 429
  );
}

/** This file cannot be uploaded to this deployment, at any size the client
 * could retry with. Thrown when direct staging is unavailable (no bucket, no
 * signing credential), which makes the multipart cap the real limit.
 *
 * A distinct type rather than a bare `Error` because retryability is decided by
 * type: `AddSourcesModal` only treats a permanent `ApiError` as non-retryable,
 * so a plain `Error` here would offer a Retry button whose every click reissues
 * the same failing `/direct/start`. The size rejection made at file-pick time
 * is already non-retryable — the two must agree. */
export class UploadTooLargeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UploadTooLargeError";
  }
}

/** Run a direct upload, translating "this deployment can't stage bytes" into
 * the plain size message.
 *
 * A deployment with no bucket (or no signing credential) can never accept this
 * file, so the honest thing to tell the user is that it's too large — the
 * multipart cap is the real limit there. Shared by both entry points so a
 * bucket-less environment behaves the same whichever one the caller used. */
async function directOrTooLarge(file: File, run: () => Promise<Upload>): Promise<Upload> {
  try {
    return await run();
  } catch (e) {
    if (e instanceof DirectUploadUnavailableError) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      const limitMb = MAX_UPLOAD_BYTES / (1024 * 1024);
      throw new UploadTooLargeError(
        `"${file.name}" is ${mb} MB — over the ${limitMb} MB upload limit.`,
      );
    }
    throw e;
  }
}

export type UploadProgress = { loaded: number; total: number; pct: number };

/** The body finished uploading but the server didn't respond before the
 * finishing watchdog fired. The upload may still be completing server-side
 * (the API keeps processing after the body is received), so this is
 * deliberately NOT retryable — retrying would risk creating a duplicate
 * Source. Callers should revalidate the Sources list instead. */
export class UploadStillProcessingError extends Error {
  constructor() {
    super("Still finishing — this may have completed. Check your Sources or refresh.");
    this.name = "UploadStillProcessingError";
  }
}

/** The request failed with no HTTP response, before the body was sent — so
 * nothing reached the server and nothing was created.
 *
 * Deliberately NOT an `ApiError` and not one of the two permanent types above,
 * so `isPermanentUploadFailure` reports it retryable: re-picking the file is
 * exactly the right recovery and cannot duplicate a Source, because the server
 * never saw the first attempt.
 *
 * A distinct type (rather than the bare `Error` this used to throw) so the
 * failure can be classified for telemetry without string-matching the
 * message — see `describeUploadFailure`. */
export class UploadTransferError extends Error {
  /** `navigator.onLine` at the moment of failure, when it could be read.
   *
   * Carried on the error rather than left at the throw site so the classifier
   * can keep the two apart: "the user's network was gone" is not our defect and
   * "something else stopped the body" is, and a dashboard that merges them
   * cannot tell which one is happening. The user copy already branches on this;
   * discarding it for analytics threw away the one split we can actually
   * establish. */
  readonly online?: boolean;

  constructor(message: string, online?: boolean) {
    super(message);
    this.name = "UploadTransferError";
    this.online = online;
  }
}

/** A coarse, stable classification of an upload failure, for analytics.
 *
 * Reasons are deliberately few and never derived from message text, so a copy
 * change can't silently re-bucket a metric. `status` is present only when the
 * server actually answered.
 *
 * `phase` says how far the attempt got, and its job is to separate "the server
 * cannot be holding this" from "the server may be". Derived here rather than at
 * each call site: four surfaces hardcoded it to "transfer", so the split
 * reported no finishing-phase failures at all — a field that can only ever hold
 * one value is worse than no field.
 *
 * A server *answer* is not the ambiguous case: an `ApiError` means the request
 * completed and the response is authoritative about what happened, so it stays
 * "transfer". `finishing` is reserved for the genuinely unresolved outcome —
 * the body landed and no answer came — which is exactly what
 * `UploadStillProcessingError` is thrown for, and why that error is the one the
 * codebase already refuses to retry. */
export function describeUploadFailure(
  e: unknown,
): { reason: string; status?: number; phase: "preflight" | "transfer" | "finishing" } {
  // No bytes ever move for this one — it is decided before the request — so
  // labelling it "transfer" was simply wrong.
  if (e instanceof UploadTooLargeError) return { reason: "too_large", phase: "preflight" };
  // Only reachable once `bodySent` flipped, so the server may already hold it.
  if (e instanceof UploadStillProcessingError) {
    return { reason: "still_processing", phase: "finishing" };
  }
  if (e instanceof UploadTransferError) {
    // A definite offline reading is its own bucket: nothing on our side caused
    // it and nothing on our side fixes it. `online === true` proves nothing
    // (the browser is often optimistic), so it stays with the unknown case.
    return { reason: e.online === false ? "offline" : "transfer_failed", phase: "transfer" };
  }
  // The direct path's transport failures. `terminal` already draws the line
  // that matters: GCS refused the session outright (expired, bad range) versus
  // the connection dropped, which is the direct-path twin of the branch above
  // and belongs in the same bucket. Without this every >32 MB failure read as
  // "unknown" with its status thrown away.
  if (e instanceof ResumableUploadError) {
    return {
      reason: e.terminal ? "upload_rejected" : "transfer_failed",
      ...(e.status === undefined ? null : { status: e.status }),
      phase: "transfer",
    };
  }
  if (e instanceof ApiError) {
    return { reason: "http_error", status: e.status, phase: "transfer" };
  }
  if (e instanceof DOMException && e.name === "AbortError") {
    return { reason: "aborted", phase: "transfer" };
  }
  // Deliberately includes the direct path's finalize-deadline error, which is a
  // bare `Error` on purpose: typing it as UploadStillProcessingError would put
  // it in the permanent set and remove a Retry that IS safe there (the staged
  // token replays rather than duplicating). "unknown" is the honest label; the
  // retryability contract is worth more than the bucket.
  return { reason: "unknown", phase: "transfer" };
}

// How long the server may take to finish an upload after the body has landed
// (the Files round-trip plus its retry budget). Armed only once the last byte
// is sent — byte transfer itself is deliberately unbounded, because a slow
// uplink is not a hang and killing it makes large uploads impossible.
const FINISHING_TIMEOUT_MS = 90_000;

// Progress-capable upload. `fetch` can't report request-body progress, so the
// interactive Sources upload uses axios (onUploadProgress) for the transfer
// mechanics — progress, abort, multipart framing. Everything railyard-specific
// stays here: shared `authHeaders` (so it tracks apiFetch), one 401-refresh
// retry, the WORKSPACE_ACCESS_DENIED recovery, and a finishing-phase watchdog.
// Rejects with a user-readable Error/ApiError (or an AbortError on cancel).
export async function uploadFileWithProgress(
  file: File,
  opts: {
    tags?: string[];
    projectId?: string;
    scope?: "project_knowledge" | "task_attachment";
    onProgress?: (p: UploadProgress) => void;
    signal?: AbortSignal;
    // Stable across retries of the same upload — see UploadRetryState.
    retry?: UploadRetryState;
  } = {},
): Promise<Upload> {
  // Files past the request-body cap can't reach Cloud Run at all; stage them
  // straight into GCS instead (ENG-1820). Same return shape, same progress and
  // cancel contract, so callers don't branch. A deployment with no staging
  // bucket falls back to the old "too large" message rather than a bare error
  // — there is nothing a retry could fix there. That fallback keys off 501
  // specifically, NOT any failure: a transient 503 stays retryable.
  if (usesDirectUpload(file)) {
    // A caller that threads its own `UploadRetryState` (the Sources modal)
    // keeps the token across retries of this attempt. Everyone else gets a
    // fresh state per call — so a rejected promise that the user recovers
    // from by re-picking starts at `/start` again. Closing that gap for all
    // callers is ENG-3487; the module-level registry that briefly lived here
    // was cut after eight defects in five review rounds.
    const run = (resume: UploadRetryState) =>
      uploadFileDirect(file, {
        tags: opts.tags,
        projectId: opts.projectId,
        scope: opts.scope,
        onProgress: opts.onProgress,
        signal: opts.signal,
        resume,
      });
    const retry = opts.retry ?? newUploadRetryState();
    return directOrTooLarge(file, async () => {
      try {
        return await run(retry);
      } catch (e) {
        // See `isDeadResumeToken`: a 404 on a token we were RESUMING means the
        // server no longer has it, so holding on would wedge this file — every
        // attempt would jump back to `/complete` and 404 again. Clear it and
        // run the upload properly, once. Only reachable for a caller that
        // threads its own state, since that is the only way a token survives to
        // a second call.
        if (!isDeadResumeToken(retry, e)) throw e;
        retry.token = undefined;
        retry.staged = undefined;
        return await run(retry);
      }
    });
  }

  const url = `${API}/api/uploads`;
  const fd = new FormData();
  fd.append("file", file);
  fd.append("tags", (opts.tags ?? []).join(","));
  if (opts.projectId) fd.append("project_id", opts.projectId);
  if (opts.scope) fd.append("scope", opts.scope);

  const attempt = async (headers: Headers): Promise<Upload> => {
    // Combine the caller's cancel signal with a finishing-phase watchdog: byte
    // transfer is unbounded (slow uplinks shouldn't be killed), but once the
    // body is sent we cap the server-side Files round-trip so a hang surfaces
    // as a retryable timeout rather than stranding the row.
    const controller = new AbortController();
    let finishTimer: ReturnType<typeof setTimeout> | undefined;
    let timedOut = false;
    // Once the body is fully sent the server may create the Source regardless
    // of what the client does, so any *post-body* failure (watchdog timeout or
    // a dropped connection before the response) is non-retryable — retrying
    // would risk a duplicate. Pre-body failures are safe to retry.
    let bodySent = false;
    // A 401-refresh retry runs this a second time. Callers classify an abort by
    // the last reported pct (>= 100 means post-body), so attempt 1's 100% must
    // not survive into attempt 2 before it has sent a byte (ENG-4336).
    opts.onProgress?.({ loaded: 0, total: file.size, pct: 0 });
    if (opts.signal) {
      if (opts.signal.aborted) controller.abort();
      else opts.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    // Don't forward Content-Type — axios sets the multipart boundary itself.
    const headerObj: Record<string, string> = {};
    headers.forEach((value, key) => {
      if (key.toLowerCase() !== "content-type") headerObj[key] = value;
    });
    try {
      const resp = await axios.post<Upload>(url, fd, {
        headers: headerObj,
        signal: controller.signal,
        onUploadProgress: (e: AxiosProgressEvent) => {
          const total = e.total ?? file.size;
          const loaded = Math.min(e.loaded, total);
          // Only report 100% once the body is fully sent — otherwise rounding
          // could show 100% at ~99.5%, and the UI hides Cancel / shows
          // "Finishing…" at pct>=100 while the watchdog (armed at loaded>=total)
          // isn't running yet, stranding a stalled final chunk.
          const pct = !total
            ? 0
            : loaded >= total
              ? 100
              : Math.min(99, Math.floor((loaded / total) * 100));
          opts.onProgress?.({ loaded, total, pct });
          if (loaded >= total && !bodySent) {
            bodySent = true;
            finishTimer = setTimeout(() => {
              timedOut = true;
              controller.abort();
            // Armed only once the bytes are sent, so a slow uplink transferring
            // a large file is never killed mid-transfer.
            }, FINISHING_TIMEOUT_MS);
          }
        },
      });
      return resp.data;
    } catch (err) {
      const e = err as AxiosError;
      if (axios.isCancel(e) || e.code === "ERR_CANCELED") {
        // Watchdog abort → the body was already sent and the server may still
        // be creating the Source, so this is non-retryable (revalidate, don't
        // re-upload). User Cancel → AbortError (the modal swallows it).
        if (timedOut) throw new UploadStillProcessingError();
        throw new DOMException("Upload canceled", "AbortError");
      }
      if (e.response) {
        const status = e.response.status;
        const data = e.response.data;
        let detail = `Upload failed (HTTP ${status}).`;
        const d = (data as { detail?: unknown })?.detail;
        if (typeof d === "string") detail = d;
        // Stale/revoked active workspace → drop it + reload, same as apiFetch.
        // The reload tears the page down, so leave the promise pending to avoid
        // flashing an error first.
        if (status === 403 && recoverIfWorkspaceDenied(data)) {
          return new Promise<Upload>(() => {});
        }
        throw new ApiError({ status, url, detail, body: data });
      }
      // No response = network failure. If the body was already fully sent, the
      // server may still be creating the Source, so this is non-retryable
      // (revalidate, don't duplicate) — same as the finishing watchdog. A
      // failure mid-transfer is safe to retry (nothing was stored).
      //
      // Nothing that reaches here can be a size problem: anything over
      // MULTIPART_SIZE_LIMIT returned down the direct-to-GCS path above, so
      // this branch only ever sees files within the multipart cap. The message
      // therefore never mentions size — see `transferFailureMessage` and
      // ENG-5238 for what offering it as an explanation cost.
      if (bodySent) throw new UploadStillProcessingError();
      // `navigator.onLine` is false only when the browser is certain it has no
      // network, so a false reading is worth reporting; a true one proves
      // nothing and falls through to the generic copy and the generic reason.
      const online = typeof navigator === "undefined" ? undefined : navigator.onLine;
      throw new UploadTransferError(transferFailureMessage(file, { online }), online);
    } finally {
      if (finishTimer !== undefined) clearTimeout(finishTimer);
    }
  };

  // Mirror apiFetch's 401 recovery: a stale Firebase ID token (cached up to an
  // hour) 401s on first use, so force a refresh and retry once.
  // This transport builds its own headers and dispatches through Axios, so it
  // does not inherit apiFetch's tenant gate. An upload is a mutation, so it
  // needs the same one: started while a re-resolution is pending, it would be
  // created in whatever tenant the pinless headers resolve to (codex P1). The
  // check is repeated after the headers are built, because `authHeaders` awaits
  // Firebase and a recovery can begin during that await.
  // The workspace pin alone does not identify the tenant an upload will land
  // in: two impersonation targets can belong to the same workspace, so the
  // effective user can change while `X-Active-Workspace` stays put. Compare the
  // impersonation header too, matching apiFetch's pin-plus-session check
  // (codex P1).
  const identityOf = (headers: Headers) =>
    `${headers.get("X-Active-Workspace") ?? ""}|${headers.get("X-Impersonate") ?? ""}`;

  const authedForCurrentTenant = async (forceRefresh = false): Promise<Headers> => {
    for (let attemptNo = 0; ; attemptNo++) {
      await awaitTenantSettled();
      const headers = await authHeaders(url, {}, forceRefresh);
      if (!tenantContextInDoubt()) return headers;
      if (attemptNo >= 2) throw new TenantContextUnresolvedError();
    }
  };

  const attemptWithAuth = async (): Promise<Upload> => {
    const first = await authedForCurrentTenant();
    try {
      return await attempt(first);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        const refreshed = await authedForCurrentTenant(true);
        // An upload creates a Source in whichever workspace it is sent to, so a
        // token refresh must not double as a tenant change: if recovery or an
        // in-place switch moved us since the first attempt, refuse rather than
        // create the file in an organization the user never chose (codex P1).
        if (identityOf(refreshed) !== identityOf(first)) throw new TenantChangedError();
        return attempt(refreshed);
      }
      throw e;
    }
  };

  return attemptWithAuth();
}

// Server-side URL → Source: the backend hands the URL to Firecrawl (which
// fetches + renders it and returns Markdown), then stores it as a
// project_knowledge Source. Returns the created Upload, same shape as a file
// upload. The fetch happens on Firecrawl's side, so there's no SSRF surface.
export async function addSourceFromUrl(
  url: string,
  opts?: { projectId?: string; title?: string },
): Promise<Upload> {
  try {
    return await requestJson(`${API}/api/uploads/from-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        url,
        project_id: opts?.projectId,
        title: opts?.title,
      }),
    });
  } catch (e) {
    // A clean HTTP error (ApiError, incl. AuthError) means the server
    // responded and no Source was created — safe to surface and retry.
    if (e instanceof ApiError) throw e;
    // A network-level failure (no response) is ambiguous: the request was
    // sent, so Firecrawl may have scraped and the Source may already be
    // stored. Mirror the file path's post-body case — non-retryable, so a
    // blind resubmit can't duplicate the Source / double-charge Firecrawl.
    // The caller revalidates the Sources list instead.
    throw new UploadStillProcessingError();
  }
}

/** Save the existing binary in place; retrying preserves its file_id. */
export async function promoteUpload(fileId: string, projectId: string): Promise<Upload> {
  return requestJson(`${API}/api/uploads/${encodeURIComponent(fileId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ scope: "project_knowledge", project_id: projectId }),
  });
}

export async function updateUploadTags(fileId: string, tags: string[]): Promise<Upload> {
  return requestJson(`${API}/api/uploads/${encodeURIComponent(fileId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tags }),
  });
}

export async function deleteUpload(fileId: string): Promise<void> {
  await requestOk(
    `${API}/api/uploads/${encodeURIComponent(fileId)}`,
    { method: "DELETE" },
  );
}

export async function linkProjectSource(projectId: string, fileId: string): Promise<Upload> {
  return requestJson(
    `${API}/api/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(fileId)}`,
    { method: "PUT" },
  );
}

export async function unlinkProjectSource(projectId: string, fileId: string): Promise<void> {
  await requestOk(
    `${API}/api/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(fileId)}`,
    { method: "DELETE" },
  );
}

// Auth-gated endpoint serving a source's raw bytes by its stable file_id.
// Keyed by id (not the agent mount path) so two same-named Sources stay
// distinct and names the mount-path sanitizer rewrites still resolve. The
// URL needs our Authorization + X-Active-Workspace headers, so it can't go
// straight into an `<a href>` / `<img src>` — callers fetch it through the
// authed-blob helpers (`useAuthedBlobUrl` for preview, `triggerAuthedDownload`
// for download).
export function uploadContentUrl(upload: Pick<Upload, "file_id">): string {
  return `${API}/api/uploads/${encodeURIComponent(upload.file_id)}/content`;
}

// Auth-gated endpoint serving a browser-previewable PDF rendering of a Source.
// PDFs pass through unchanged; office docs (PPTX/DOCX/XLSX/…) are rendered to
// PDF server-side via LibreOffice. Fetched through the authed-blob helpers like
// uploadContentUrl. Returns 415 for types the browser previews natively
// (images) and 502 when no rendering is available.
export function uploadPreviewUrl(upload: Pick<Upload, "file_id">): string {
  return `${API}/api/uploads/${encodeURIComponent(upload.file_id)}/preview`;
}

/** Trigger a browser download of a source under its original filename. */
export async function downloadUpload(
  upload: Pick<Upload, "file_id" | "filename">,
): Promise<void> {
  await triggerAuthedDownload(uploadContentUrl(upload), upload.filename);
}

/** Agent mount path → authenticated content bytes (uploads + owned artifacts). */
export function fileContentUrl(path: string): string {
  return `${API}/api/files/content?path=${encodeURIComponent(path)}`;
}

/**
 * Resolve a session mount path to its Source row. The mount path is the
 * sanitizer-aware path the agent sees (`My Deck.pdf` → `My-Deck.pdf`), so
 * basename-only lookups via `/api/files/content` can 404 or collide.
 */
export { resolveUploadByMountPath } from "../upload-mount-path";

export async function fetchFileContentText(
  path: string,
  init?: { signal?: AbortSignal },
): Promise<string> {
  const r = await requestOk(fileContentUrl(path), { signal: init?.signal });
  return r.text();
}

export async function fetchUploadContentText(
  fileId: string,
  init?: { signal?: AbortSignal },
): Promise<string> {
  const r = await requestOk(uploadContentUrl({ file_id: fileId }), {
    signal: init?.signal,
  });
  return r.text();
}
